/**
 * What an answer is called on the wire, wherever one arrives.
 *
 * A form posted from `/user/fuxi/` and a JSON batch posted by a client that
 * has been offline say the same things about a card, and both are read by
 * name. The names live here so that the two readers cannot come to disagree
 * about them, and so that the markup writing a hidden input is writing the
 * name the reader of it looks for.
 *
 * `src/fuxi/posted-answer.ts` reads the client's shape and
 * `src/fuxi/typed-answer.ts` the form's. What separates them is who decides
 * the rating: a client that ran the same scheduler states one, and a reader
 * typing into a page states an answer that this site marks.
 */

/** What kind of subject the card is about. */
export const kindField = "kind";

/** Which subject, which for a 词典 card is the headword itself. */
export const subjectField = "subjectId";

/** Which question was asked, which decides how the answer is marked. */
export const modeField = "mode";

/**
 * The id of the answer, which is what makes a second delivery a no-op.
 *
 * **A client mints its own, and a page is given one when its card is drawn.**
 * A page with no script cannot make a UUID, and the id has to exist before the
 * reader presses anything for a repeated press to be recognised as one. So the
 * id belongs to the drawn card: a reader who double-presses, or who goes back
 * and posts the page again, posts the id their card was drawn with, and
 * `ReviewAnswers` finds it already on the card and changes nothing.
 */
export const eventField = "eventId";

/**
 * What the reader typed, which is the whole of what a card is graded on.
 *
 * **A handwriting card posts its ink here.** The ink is described in the
 * format the published index uses, which is three letters per straight run of
 * each stroke, so what arrives is a short string of letters rather than a
 * word. It is the reader's answer either way, it is graded the way everything
 * else in this field is graded, and it travels in the field the form already
 * has. See `src/handwriting/handwriting-line.ts` and
 * `src/fuxi/grade-handwriting.ts`.
 */
export const typedField = "typed";

/**
 * What the card was asked through, where it was asked through anything.
 *
 * The one field here that decides nothing. A handwriting card about 然 is asked
 * through some word of the reader's own, and a cloze card about 突然 is asked
 * through a 例句 of theirs, named by its uuid. The marked card shows the same
 * one again with the answer back in place, which is the thing the reader was
 * reaching for.
 *
 * The card itself names its subject, so this is context and never identity: it
 * is checked against the reader's own list on the way back in, and a card whose
 * cue does not survive that check is marked and shown without it.
 */
export const cueField = "cue";

/**
 * Whether the reader opened the card's hint before answering.
 *
 * Set by the page's own script when the hint is opened, and carried back by a
 * card drawn again for another try. A card answered with it set earns 还行 at
 * best. The script is also what shows the hint, so a page that cannot set this
 * field draws no hint to open. See `src/site/fuxi-hint.ts`.
 */
export const hintField = "hinted";

/** What the hint field holds once the hint is open. */
export const hintedValue = "1";

/**
 * The word the reader typed first and was asked to try again after.
 *
 * Present only on a card drawn again because the first answer was another word
 * meaning what the card showed. Its presence is what makes the second answer
 * final, and it is the word the card names under "Not" next time. See
 * `src/user/fuxi-rival.ts`.
 */
export const declinedField = "declined";

/**
 * The recording a speech card posts, where the reader said the word.
 *
 * 16-bit mono samples at 16 kHz in base64, as `/user/pronounce/` takes them.
 * Empty where the reader typed instead, and the card is then marked on the
 * typed field. On a speech card drawn again after a miss, `declined` holds
 * what the first try sounded like, which makes the second try final. See
 * `src/user/fuxi-speech-answering.ts`.
 */
export const spokenField = "spoken";

/** The rating a client that graded the answer itself states. */
export const ratingField = "rating";

/** When the reader answered, by the clock of whatever they answered on. */
export const reviewedAtField = "reviewedAt";

/** How long they took, where the client measured it. */
export const durationField = "durationMs";
